import { create } from 'zustand'
import { readViewPref, writeViewPref } from '../lib/viewPrefs'
import { callTool } from '../lib/tools'
import { placeOf, type Located, type Place } from '../graph/mapPlaces'

/**
 * The graph or the map (nobgp-router#1934), and where the VIEWER is.
 *
 * The map draws each node at the place of its public address. A node with no place of its own,
 * and the user's own icon, are drawn at the viewer's place. The router gives that place in
 * `whoami`, from the address this browser called from: a browser can give it only behind a
 * permission prompt.
 */
export interface GraphViewState {
  /** Draw the nodes on a map. A view preference, remembered (`lib/viewPrefs.ts`). */
  mapView: boolean
  setMapView: (on: boolean) => void
  /**
   * `undefined`: not asked yet, or the call failed. `null`: asked (or the call is in progress),
   * and the router has no place for this address (a private address, or a router that predates
   * the field). The map then draws no user icon, and no node that has no place of its own.
   */
  viewer?: Place | null
  loadViewer: () => Promise<void>
}

const useGraphView = create<GraphViewState>()((set, get) => ({
  // ⚠ Read at module scope: `readViewPref` is the guarded reader.
  mapView: readViewPref('mapView'),
  setMapView: on => {
    set({ mapView: on })
    writeViewPref('mapView', on)
  },
  loadViewer: async () => {
    if (get().viewer !== undefined) return
    // Claimed before the call, so two renders in a row ask once.
    set({ viewer: null })
    try {
      const who = await callTool<{ location?: Located }>('whoami')
      set({ viewer: placeOf(who?.location) })
    } catch (error) {
      console.warn('map: the viewer location is not available', error)
      // ⚠ Not asked, and not "asked, and there is none": `null` is an ANSWER. With `null` kept
      // here, one failed call (a network error, a token that was being refreshed) was the last
      // call of the tab's life, and the map had no user icon until a reload. The next time the map
      // is shown asks again.
      set({ viewer: undefined })
    }
  },
}))

export default useGraphView
